import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";

type Variant = "primary" | "secondary" | "ghost";

const variants: Record<Variant, string> = {
  primary:
    "bg-gold-500 text-[#070707] shadow-glow hover:brightness-110 focus-visible:ring-gold-300",
  secondary:
    "border border-gold-300/30 bg-button-metal text-ink-100 hover:border-gold-300/60 hover:bg-gold-300/12 focus-visible:ring-gold-300",
  ghost:
    "border border-transparent text-gold-300 hover:border-gold-300/30 hover:bg-gold-300/10 focus-visible:ring-gold-300",
};

const baseClass =
  "inline-flex min-h-12 items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950 disabled:cursor-not-allowed disabled:opacity-60";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: Variant;
  showArrow?: boolean;
}

interface LinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  children: ReactNode;
  to: string;
  variant?: Variant;
  showArrow?: boolean;
}

export function Button({
  children,
  className = "",
  variant = "primary",
  showArrow = false,
  ...props
}: ButtonProps) {
  return (
    <button
      className={`${baseClass} ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
      {showArrow ? <ArrowRight aria-hidden="true" className="h-4 w-4" /> : null}
    </button>
  );
}

export function LinkButton({
  children,
  className = "",
  to,
  variant = "primary",
  showArrow = true,
  ...props
}: LinkButtonProps) {
  return (
    <Link
      className={`${baseClass} ${variants[variant]} ${className}`}
      to={to}
      {...props}
    >
      {children}
      {showArrow ? <ArrowRight aria-hidden="true" className="h-4 w-4" /> : null}
    </Link>
  );
}
